import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import BrandLayout from "@/components/layout/BrandLayout";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, User, MapPin, Calendar, Hash, PlayCircle, CheckCircle, XCircle, BarChart3 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const BrandSubmissionDetail = () => {
  const { id } = useParams();
  const { token } = useAuth();
  const navigate = useNavigate();
  const [submission, setSubmission] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionDialog, setActionDialog] = useState({ open: false, type: null });

  const fetchSubmission = async () => {
    try {
      const response = await axios.get(`${API}/brand/submissions/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubmission(response.data);
    } catch (error) {
      console.error("Failed to fetch submission:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSubmission();
  }, [id, token]);

  const handleApprove = async () => {
    try {
      await axios.put(
        `${API}/brand/submissions/${id}/approve`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Submission approved! Artist has been notified.");
      fetchSubmission();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to approve submission");
    } finally {
      setActionDialog({ open: false, type: null });
    }
  };

  const handleReject = async () => {
    try {
      await axios.put(
        `${API}/brand/submissions/${id}/reject`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Submission rejected");
      navigate("/brand/submissions");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reject submission");
    } finally {
      setActionDialog({ open: false, type: null });
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending_brand_approval: "bg-[#F58220]/20 text-[#F58220]",
      approved: "bg-green-500/20 text-green-400",
    };
    const labels = {
      pending_brand_approval: "Pending Review",
      approved: "Approved",
    };
    return (
      <span className={`inline-flex items-center px-4 py-2 text-sm font-semibold uppercase tracking-wider ${styles[status] || "bg-gray-500/20 text-gray-400"}`}>
        {labels[status] || status}
      </span>
    );
  };

  const MediaPreview = ({ url, type, label }) => {
    const urls = Array.isArray(url) ? url : url ? [url] : [];
    
    if (urls.length === 0) {
      return (
        <div className="border border-gray-800 p-4 bg-gray-900">
          <p className="text-gray-500 text-sm uppercase tracking-wider mb-2">{label}</p>
          <div className="bg-black aspect-video flex items-center justify-center">
            <p className="text-gray-600">Not uploaded</p>
          </div>
        </div>
      );
    }

    return (
      <div className="border border-gray-800 p-4 bg-gray-900 hover:border-[#F58220] transition-colors">
        <p className="text-gray-500 text-sm uppercase tracking-wider mb-2">{label} ({urls.length})</p>
        <div className={urls.length === 1 ? "" : "grid grid-cols-2 gap-2"}>
          {urls.map((singleUrl, idx) => {
            const isVideo = type === "video" || singleUrl.includes("/video/") || singleUrl.includes(".mp4");
            const isAudio = type === "audio" || singleUrl.includes(".mp3") || singleUrl.includes(".wav");
            return (
              <div key={idx} className={`bg-black ${urls.length === 1 ? "aspect-video" : "aspect-square"} flex items-center justify-center overflow-hidden rounded`}>
                {isVideo ? (
                  <video src={singleUrl} controls className="w-full h-full object-cover" />
                ) : isAudio ? (
                  <div className="p-4 w-full"><audio src={singleUrl} controls className="w-full" /></div>
                ) : (
                  <img src={singleUrl} alt={label} className="w-full h-full object-cover" />
                )}
              </div>
            );
          })}
        </div>
        <div className="mt-2 flex flex-wrap gap-2">
          {urls.map((singleUrl, idx) => (
            <a key={idx} href={singleUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-[#F58220] hover:underline">
              {urls.length === 1 ? "Open in new tab" : `File ${idx + 1}`}
            </a>
          ))}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <BrandLayout>
        <div className="flex items-center justify-center h-64">
          <p className="text-gray-400">Loading submission...</p>
        </div>
      </BrandLayout>
    );
  }

  if (!submission) {
    return (
      <BrandLayout>
        <div className="text-center py-12">
          <p className="text-gray-400 mb-4">Submission not found</p>
          <Link to="/brand/submissions" className="text-[#F58220] hover:underline">
            Back to submissions
          </Link>
        </div>
      </BrandLayout>
    );
  }

  return (
    <BrandLayout>
      <div className="animate-fade-in" data-testid="brand-submission-detail">
        {/* Header */}
        <div className="mb-8">
          <Link 
            to="/brand/submissions" 
            className="inline-flex items-center gap-2 text-gray-400 hover:text-white mb-4"
          >
            <ArrowLeft size={20} />
            Back to submissions
          </Link>
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
            <div>
              <h1 className="text-4xl font-heading text-white mb-2">{submission.artist_name}</h1>
              <p className="text-gray-400">{submission.artist_email}</p>
            </div>
            <div className="flex items-center gap-4">
              {getStatusBadge(submission.status)}
              {submission.status === "pending_brand_approval" && (
                <div className="flex gap-2">
                  <Button
                    className="bg-green-500 hover:bg-green-600 text-white"
                    onClick={() => setActionDialog({ open: true, type: "approve" })}
                    data-testid="approve-button"
                  >
                    <CheckCircle size={18} className="mr-2" />
                    Approve
                  </Button>
                  <Button
                    variant="outline"
                    className="border-[#ED5A34] text-[#ED5A34] hover:bg-[#ED5A34] hover:text-white"
                    onClick={() => setActionDialog({ open: true, type: "reject" })}
                    data-testid="reject-button"
                  >
                    <XCircle size={18} className="mr-2" />
                    Reject
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Artist Details */}
        <div className="bg-gray-900 border border-gray-800 p-6 mb-6">
          <h2 className="font-heading text-xl text-white mb-6">ARTIST DETAILS</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-gray-800">
                <User size={20} className="text-gray-400" />
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wider">Name</p>
                <p className="font-medium text-white">{submission.artist_name}</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="p-2 bg-gray-800">
                <MapPin size={20} className="text-gray-400" />
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wider">City</p>
                <p className="font-medium text-white">{submission.city}</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="p-2 bg-gray-800">
                <MapPin size={20} className="text-gray-400" />
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wider">Venue</p>
                <p className="font-medium text-white">{submission.venue}</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="p-2 bg-gray-800">
                <Calendar size={20} className="text-gray-400" />
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wider">Date</p>
                <p className="font-medium text-white">{submission.date}</p>
              </div>
            </div>
          </div>
          <div className="mt-6 pt-6 border-t border-gray-800 flex items-start gap-3">
            <div className="p-2 bg-gray-800">
              <Hash size={20} className="text-gray-400" />
            </div>
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wider">S-User ID</p>
              <p className="font-mono text-white">{submission.s_user_id}</p>
            </div>
          </div>
        </div>

        {/* Agency Approval Info */}
        {submission.agency_approved_at && (
          <div className="bg-[#00B4E5]/20 border border-[#00B4E5] p-4 mb-6">
            <p className="text-[#00B4E5] text-sm">
              <strong>Agency Approved:</strong> {new Date(submission.agency_approved_at).toLocaleString()}
            </p>
          </div>
        )}

        {/* Media Uploads */}
        <div className="bg-gray-900 border border-gray-800 p-6 mb-6">
          <h2 className="font-heading text-xl text-white mb-6">MEDIA UPLOADS</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <MediaPreview url={submission.merchandise_url} type="image" label="Merchandise" />
            <MediaPreview url={submission.proof_of_play_url} type="video" label="Proof of Play" />
            <div className="border border-gray-800 p-4 bg-gray-900">
              <p className="text-gray-500 text-sm uppercase tracking-wider mb-2">Track Play Count</p>
              <div className="bg-black aspect-video flex items-center justify-center">
                <div className="text-center">
                  <PlayCircle size={40} className="mx-auto text-[#F58220] mb-2" />
                  <p className="text-4xl font-heading text-white">{submission.track_play_count}</p>
                  <p className="text-sm text-gray-500">times played</p>
                </div>
              </div>
            </div>
            <MediaPreview url={submission.bottle_console_url} type="video" label="Bottle next to Console" />
            <MediaPreview url={submission.branding_stories_post_url} type="image" label="Branding - Stories/Post" />
            <MediaPreview url={submission.instagram_analytics_url} type="image" label="Instagram Analytics" />
            {/* Instagram Impressions Display */}
            {(submission.instagram_analytics_url || submission.instagram_analytics_impressions > 0) && (
              <div className="bg-gradient-to-br from-purple-900/30 to-pink-900/30 border border-purple-700 p-6 flex flex-col items-center justify-center min-h-[120px]">
                <BarChart3 size={32} className="text-purple-400 mb-2" />
                <p className="text-3xl font-heading text-white">{(submission.instagram_analytics_impressions || 0).toLocaleString()}</p>
                <p className="text-sm text-gray-400">Total Impressions</p>
              </div>
            )}
          </div>
        </div>

        {/* Timestamps */}
        <div className="text-sm text-gray-500 flex flex-wrap gap-6">
          <span>Created: {new Date(submission.created_at).toLocaleString()}</span>
          <span>Updated: {new Date(submission.updated_at).toLocaleString()}</span>
          {submission.brand_approved_at && (
            <span className="text-green-400">Brand Approved: {new Date(submission.brand_approved_at).toLocaleString()}</span>
          )}
        </div>

        {/* Approval/Rejection Dialog */}
        <AlertDialog open={actionDialog.open} onOpenChange={(open) => !open && setActionDialog({ open: false, type: null })}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                {actionDialog.type === "approve" ? "Approve Submission" : "Reject Submission"}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {actionDialog.type === "approve" 
                  ? `Are you sure you want to approve this submission from ${submission?.artist_name}? The artist will be notified of the approval.`
                  : `Are you sure you want to reject this submission from ${submission?.artist_name}? The artist will be notified.`
                }
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={actionDialog.type === "approve" ? handleApprove : handleReject}
                className={actionDialog.type === "approve" ? "bg-green-500 hover:bg-green-600" : "bg-[#ED5A34] hover:bg-[#d44a28]"}
              >
                {actionDialog.type === "approve" ? "Approve" : "Reject"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </BrandLayout>
  );
};

export default BrandSubmissionDetail;
